/**
 * Document ⇄ Markdown (git sync Stage 2, card S2.1). Owner decision
 * 02.10.2026: documents live in git as readable Markdown; the mapping is in
 * Apiboo Documents › Git sync › "Git sync — Stage 2 plan".
 *
 *   documentToMarkdown(snapshotOrChildren, ctx) → string
 *   markdownToDocument(md, ctx, previousSnapshot) → { snapshot: { version: 1, blocks }, newMedia: [{ pendingRef, path }] }
 *     newMedia = files that came in through git; the sync layer uploads them
 *     (encrypted, like any attachment) and fills each node's src + key.
 *
 * EXACT by construction: every block is written, read back and compared (on
 * the editor's own normalized JSON); a block whose readable form does not
 * come back identical is written in an exact HTML form, and failing that as
 * its JSON. The whole file is then checked once more, because neighbours can
 * interact (two lists side by side would merge).
 */
import { normalize, comparable } from './docEditor';
import { blockToMarkdown, fallbackBlock, joinBlocks, FALLBACK_LANG } from './blocksToMarkdown';
import * as J from './jsonNodes';
import { withDefaults, childrenOf } from './ctx';
import { markdownToBlocks } from './markdownToBlocks';
import { generateBlockId, makeSnapshot, stableStringify } from '../../components/ui/lexicalBlocks';

export { withDefaults } from './ctx';

const same = (a, b) => stableStringify(a.map(comparable)) === stableStringify(b.map(comparable));

function writeBlock(node, ctx) {
  const target = normalize([node]);
  for (const htmlInline of [false, true]) {
    let md;
    try {
      md = blockToMarkdown(node, ctx, { htmlInline });
    } catch {
      continue;
    }
    try {
      if (same(normalize(markdownToBlocks(md, ctx)), target)) return md;
    } catch {
      /* try the next form */
    }
  }
  return fallbackBlock(node);
}

/** @param {object|Array} input  a block snapshot ({version, blocks}) or root children */
export function documentToMarkdown(input, rawCtx = {}) {
  // Known media from the NORMALIZED document: the image node cleans file names
  // (brackets go), and paths must match what is written.
  const nodes = normalize(childrenOf(input));
  const ctx = withDefaults(rawCtx, nodes);
  const parts = nodes.map((n) => writeBlock(n, ctx));

  const join = (ps) => joinBlocks(nodes, ps);

  // The whole file once more; a block that only fails next to its neighbours falls back to JSON.
  for (let guard = 0; guard < nodes.length + 1; guard += 1) {
    const back = normalize(markdownToBlocks(join(parts), ctx));
    if (same(back, nodes)) break;
    const i = nodes.findIndex((n, k) => !back[k] || stableStringify(comparable(n)) !== stableStringify(comparable(back[k])));
    if (i < 0 || parts[i].startsWith('```apiboo-block')) break;
    parts[i] = fallbackBlock(nodes[i]);
  }
  // Never hand back a file that does not read back as this document (review of #372):
  // the sync layer must stop and say so rather than commit a wrong version.
  if (!same(normalize(markdownToBlocks(join(parts), ctx)), nodes)) {
    throw new Error('docMarkdown: this document has no exact Markdown form — nothing was written');
  }
  return `${join(parts)}\n`;
}

/**
 * @param {string} md
 * @param {object} rawCtx
 * @param {object|null} previous  the document's current snapshot — unchanged blocks keep their ids
 */
export function markdownToDocument(md, rawCtx = {}, previous = null) {
  const prevBlocks = previous?.blocks || [];
  const newMedia = [];
  let prevNodes = prevBlocks.map((b) => b.node);
  try {
    prevNodes = normalize(prevNodes); // the same cleaned names the writer used
  } catch {
    /* a stored block the editor cannot load: its images are simply not "known" */
  }
  const ctx = withDefaults({ ...rawCtx, onNewMedia: (m) => newMedia.push(m) }, prevNodes);
  // Block by block: one the editor cannot load (an unknown type in hand-edited
  // JSON) stays visible as text instead of failing the whole document.
  const nodes = markdownToBlocks(md, ctx).flatMap((block) => {
    try {
      return normalize([block]);
    } catch {
      return normalize([J.code(FALLBACK_LANG, JSON.stringify(block, null, 1))]);
    }
  });
  return { snapshot: makeSnapshot(assignIds(normalize(nodes), prevBlocks)), newMedia };
}

/**
 * Ids for the new blocks: an unchanged block keeps its id (matched by content,
 * in order); a changed block between two kept ones takes the id of the old
 * block in the same place when it is the same kind; anything else is new.
 */
export function assignIds(nodes, prevBlocks) {
  const key = (n) => stableStringify(comparable(n));
  const prev = prevBlocks.map((b) => ({ id: b.id, node: b.node, key: key(normalize([b.node])[0] || b.node), used: false }));
  const ids = new Array(nodes.length).fill(null);
  // Unchanged blocks keep their id wherever they moved to (review of #372: an
  // in-order match reset every block after a moved one). Same content twice →
  // ids handed out in the old order.
  const byKey = new Map();
  for (const b of prev) {
    if (!byKey.has(b.key)) byKey.set(b.key, []);
    byKey.get(b.key).push(b);
  }
  nodes.forEach((n, i) => {
    const queue = byKey.get(key(n));
    const hit = queue && queue.find((b) => !b.used);
    if (hit) {
      hit.used = true;
      ids[i] = hit.id;
    }
  });
  // same place, same kind
  let p = 0;
  nodes.forEach((n, i) => {
    if (ids[i]) {
      const j = prev.findIndex((b) => b.id === ids[i]);
      if (j >= 0) p = j + 1;
      return;
    }
    while (p < prev.length && prev[p].used) p += 1;
    if (p < prev.length && prev[p].node?.type === n.type) {
      prev[p].used = true;
      ids[i] = prev[p].id;
      p += 1;
    }
  });
  return nodes.map((node, i) => ({ id: ids[i] || generateBlockId(), node }));
}
